import { css, jsx } from '@emotion/core';
import { Col, Form as AntForm, Icon, Input, Modal, Radio, Row, Select } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { saveSearch } from '@/src/compliance/utils';
import Button from '@/src/shared/components/Button';
import Form from '@/src/shared/components/Form';
import Heading from '@/src/shared/components/Heading';
import X from '@/src/shared/components/Icon/_icons/X';
import { bp, space } from '@/styles';
import { colMdProps, rowProps } from '@/utils/rowColHelpers';
import { multiSelectProps, singleSelectProps } from '@/utils/selectHelpers';

const { Option } = Select;

const { m } = space;

const style = {
  footerWrapper: css`
    display: flex;
    flex-direction: column;
    @media ${bp.md} {
      justify-content: space-between;
      flex-direction: row;
    }
  `,
  footerButtonWrapper: css`
    display: flex;
    flex-direction: column;
    width: 100%;
    @media ${bp.md} {
      flex-direction: inherit;
      width: auto;
    }
  `,
  cancel: css`
    margin-top: 16px;
    text-align: center;

    @media ${bp.md} {
      margin-top: 0px;
    }
  `,
  cancelInner: css`
    display: flex !important;
    align-items: baseline !important;
    text-align: center !important;
    justify-content: center;
  `,
  cancelIcon: css`
    padding-left: 8px;
  `,
  cancelText: css`
    padding-left: 0px;
  `,
  saveSearch: css`
    margin-left: 0px !important;
    margin-top: 16px;
    @media ${bp.md} {
      margin-top: 0px;
      margin-left: 8px !important;
    }
  `,
};
const AddSavedSearch = props => {
  const { visible, closeModal, searchOptions, form, data, handleSaveSearch } = props;
  const { Charts, Categories, States } = searchOptions;

  const personId = useSelector(state => state.auth.userinfo.PersonID);

  const dispatch = useDispatch();

  useEffect(() => {
    form.resetFields();
  }, [data]);

  const onSave = async () => {
    await form.validateFieldsAndScroll(async (err, values) => {
      if (err) return;
      const formattedPayload = saveSearch({
        personId,
        values,
        form,
        options: searchOptions,
        initialData: {
          ...data,
          PersonId: personId,
        },
      });

      dispatch(handleSaveSearch({ payload: formattedPayload }));
    });
  };

  const renderMultiSelect = ({
    options,
    optCode,
    optDescription,
    label,
    _props = {},
    initialValue = [],
    field,
  }) => {
    const { getFieldDecorator } = form;

    return (
      <Col {...colMdProps}>
        <AntForm.Item label={label}>
          {getFieldDecorator(field, {
            initialValue,
          })(
            <Select {...multiSelectProps(false)} {..._props}>
              {options.map(opt => (
                <Option value={opt[optCode]} key={`${opt[optDescription]}-${label}`}>
                  {opt[optDescription]}
                </Option>
              ))}
            </Select>
          )}
        </AntForm.Item>
      </Col>
    );
  };

  return (
    <Modal
      visible={visible}
      onCancel={closeModal}
      width="80%"
      maskStyle={{
        backgroundColor: 'rgba(0,38,73,0.65)',
      }}
      title={
        <Heading type={3} weight="bolder" theme="gray" css={m.b0}>
          Save Search
        </Heading>
      }
      footer={[
        <div css={style.footerWrapper} key="delete-button">
          {/* <span css={style.footerButtonWrapper}>
                <Button
                  size="small"
                  theme="red"
                  onClick={() => {}}
                >
                  <Icon type="delete" />
                  Delete Saved Search
                </Button>
              </span> */}
          <span css={style.footerButtonWrapper}>
            <Button
              theme="gray"
              size="small"
              css={style.cancel}
              disabled={false}
              onClick={closeModal}
            >
              <span css={style.cancelInner}>
                <span css={style.cancelIcon}>
                  <X css={style.cancelIcon} />
                </span>
                <span css={style.cancelIcon}>Cancel</span>
              </span>
            </Button>
            <Button
              disabled={false}
              size="small"
              theme="default"
              css={style.saveSearch}
              htmlType="submit"
              onClick={onSave}
            >
              <Icon type="save" theme="filled" />
              Save Search
            </Button>
          </span>
        </div>,
      ]}
    >
      <Form>
        <Row {...rowProps}>
          <Col {...colMdProps}>
            <Heading type={3} weight="bolder" css={m.b24} theme="primaryGray">
              Search Name
            </Heading>
          </Col>
        </Row>
        <Row {...rowProps}>
          <Col {...colMdProps}>
            <AntForm.Item label="Name of Search">
              {form.getFieldDecorator('SearchDescription', {
                initialValue: '',
                rules: [
                  {
                    message: 'Enter Name of Search.',
                    required: true,
                  },
                ],
              })(<Input />)}
            </AntForm.Item>
          </Col>
          <Col {...colMdProps}>
            <AntForm.Item label="Show on homepage">
              {form.getFieldDecorator('IncludeOnHome', {
                initialValue: data.IncludeOnHome,
              })(
                <Radio.Group>
                  <Radio value="Y">Yes</Radio>
                  <Radio value="N">No</Radio>
                </Radio.Group>
              )}
            </AntForm.Item>
          </Col>
        </Row>
        <Row {...rowProps}>
          <Col {...colMdProps}>
            <Heading type={3} weight="bolder" css={m.b24} theme="primaryGray">
              Search Filters
            </Heading>
          </Col>
        </Row>
        <Row {...rowProps}>
          <Col {...colMdProps}>
            <AntForm.Item label="Search Text">
              {form.getFieldDecorator('FieldOptions.SearchText', {
                initialValue: data.FieldOptions.SearchText,
                rules: [
                  {
                    required: false,
                  },
                ],
              })(<Input />)}
            </AntForm.Item>
          </Col>
          {renderMultiSelect({
            label: 'State',
            options: States,
            optCode: 'StateCode',
            optDescription: 'StateDescription',
            field: 'FieldOptions.States',
            initialValue: data.FieldOptions.States,
          })}
        </Row>
        <Row {...rowProps}>
          <Col {...colMdProps}>
            <AntForm.Item label="Category">
              {form.getFieldDecorator('FieldOptions.Category', {
                initialValue: data.FieldOptions.Categories,
              })(
                <Select {...singleSelectProps(false)}>
                  {Categories.map(category => (
                    <Option value={category.CategoryId} key={`${category.CategoryId}-category`}>
                      {category.CategoryDescription}
                    </Option>
                  ))}
                </Select>
              )}
            </AntForm.Item>
          </Col>
          {renderMultiSelect({
            label: 'Charts',
            options: Charts,
            optCode: 'ChartId',
            optDescription: 'ChartTitle',
            field: 'FieldOptions.Charts',
            initialValue: data.FieldOptions.Charts,
          })}
        </Row>
      </Form>
    </Modal>
  );
};

AddSavedSearch.propTypes = {};

export default AntForm.create()(AddSavedSearch);
